import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Link2,
  RefreshCw,
  Shield,
  Activity,
  ListTree,
  Server,
  Radio,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getUnifiedThreatDashboard,
  getCyberPhysicalCorrelation,
  getSecurityAccessLogs,
  getCyberSecurityAlerts,
  createCyberSecurityAlert,
  UnifiedThreatDashboardData,
  UnifiedThreatZoneAgg,
  CyberPhysicalCorrelationData,
  CyberPhysicalCorrelationRow,
  CyberSecurityAlertRecord,
  CyberAlertCategory,
} from "../../../service/securityService";

const CyberPhysicalHub: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [corrWindow, setCorrWindow] = useState(60);
  const [unified, setUnified] = useState<UnifiedThreatDashboardData | null>(null);
  const [correlation, setCorrelation] = useState<CyberPhysicalCorrelationData | null>(
    null
  );
  const [deniedLogs, setDeniedLogs] = useState<Record<string, unknown>[]>([]);
  const [cyberAlerts, setCyberAlerts] = useState<CyberSecurityAlertRecord[]>(
    []
  );
  const [logForm, setLogForm] = useState({
    title: "",
    alertCategory: "it_security" as CyberAlertCategory,
    severity: "Medium",
    zone: "",
    source: "Manual",
  });
  const [logging, setLogging] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [u, c, logs, cyber] = await Promise.all([
        getUnifiedThreatDashboard(days),
        getCyberPhysicalCorrelation(corrWindow),
        getSecurityAccessLogs({ limit: 40, accessType: "Denied" }),
        getCyberSecurityAlerts({ limit: 40 }),
      ]);
      setUnified(u);
      setCorrelation(c);
      setDeniedLogs(logs.data);
      setCyberAlerts(cyber.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load cyber-physical data");
    } finally {
      setLoading(false);
    }
  }, [days, corrWindow]);

  useEffect(() => {
    load();
  }, [load]);

  const heatColor = (score: number) => {
    if (score >= 70) return "bg-red-500";
    if (score >= 40) return "bg-amber-500";
    if (score >= 15) return "bg-yellow-400";
    return "bg-emerald-500";
  };

  const recentLabel = (t: Record<string, unknown>) => {
    if (t.incidentType) return String(t.incidentType);
    if (t.accessType) return `Access ${t.accessType}`;
    if (t.breachType) return String(t.breachType || "Boundary breach");
    return "Threat event";
  };

  const categoryBadge = (c?: string) => {
    if (c === "network_intrusion") return "NIDS";
    if (c === "siem_other") return "SIEM";
    return "IT";
  };

  const submitInternalAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logForm.title.trim()) {
      toast.warn("Enter a title");
      return;
    }
    setLogging(true);
    try {
      await createCyberSecurityAlert({
        title: logForm.title.trim(),
        alertCategory: logForm.alertCategory,
        severity: logForm.severity,
        zone: logForm.zone.trim() || undefined,
        source: logForm.source.trim() || "Manual",
      });
      toast.success("Alert recorded");
      setLogForm((f) => ({
        ...f,
        title: "",
        zone: "",
      }));
      await load();
    } catch {
      toast.error("Could not record alert");
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Link2 className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Cyber-Physical Security Integration
            </h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Unified threat view merges physical security data with IT, SIEM, and
              network-intrusion alerts stored for your tenant. Connectors can POST
              into the cyber-alert store; correlation matches incidents, denials,
              and alerts in time and zone.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-gray-600 flex items-center gap-2">
            Period
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border rounded-lg px-2 py-1 text-sm"
            >
              {[1, 7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d}d
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600 flex items-center gap-2">
            Corr. window (min)
            <select
              value={corrWindow}
              onChange={(e) => setCorrWindow(Number(e.target.value))}
              className="border rounded-lg px-2 py-1 text-sm"
            >
              {[15, 30, 60, 120].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full text-sm hover:bg-teal-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate("/security/command-center")}
            className="px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
          >
            Command Center
          </button>
        </div>
      </div>

      {loading && !unified ? (
        <div className="flex justify-center py-20">
          <SpinLoader />
        </div>
      ) : (
        <>
          {unified?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                {
                  label: "Total threats",
                  value: unified.summary.totalThreats,
                  icon: Activity,
                  tone: "text-gray-900",
                },
                {
                  label: "Critical",
                  value: unified.summary.criticalThreats,
                  icon: Shield,
                  tone: "text-red-600",
                },
                {
                  label: "High",
                  value: unified.summary.highThreats,
                  icon: Shield,
                  tone: "text-orange-600",
                },
                {
                  label: "IT / NIDS (period)",
                  value: unified.summary.cyberAlertsInPeriod ?? 0,
                  icon: Radio,
                  tone: "text-indigo-600",
                },
                {
                  label: "Locations",
                  value: unified.summary.locations,
                  icon: ListTree,
                  tone: "text-[#0fa9a6]",
                },
              ].map(({ label, value, icon: Icon, tone }) => (
                <div
                  key={label}
                  className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className={`text-2xl font-bold ${tone}`}>{value}</p>
                  </div>
                  <Icon className="w-8 h-8 text-gray-200" />
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Unified threat heatmap (by location)
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Aggregated from incidents, denied access, boundary breaches,
                geofence violations, and unauthorized asset movements (
                {unified?.period ?? `${days} days`}).
              </p>
              {!unified?.threatMap?.length ? (
                <p className="text-gray-500 text-sm">No threat aggregates in this period.</p>
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                  {(unified.threatMap as UnifiedThreatZoneAgg[]).map((z) => (
                    <div key={z.location}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-gray-800 truncate max-w-[70%]">
                          {z.location}
                        </span>
                        <span className="text-gray-500">
                          score {Math.round(z.threatScore)} · {z.totalThreats}{" "}
                          evt
                          {(z.cyberAlerts ?? 0) > 0
                            ? ` · ${z.cyberAlerts} cyber`
                            : ""}
                        </span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${heatColor(
                            z.threatScore
                          )}`}
                          style={{ width: `${Math.min(100, z.threatScore)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Recent unified feed
              </h2>
              {!unified?.recentThreats?.length ? (
                <p className="text-gray-500 text-sm">No events in the last 24h window.</p>
              ) : (
                <ul className="space-y-2 max-h-[420px] overflow-y-auto text-sm">
                  {unified.recentThreats.map((t, i) => (
                    <li
                      key={(t._id as string) || (t.id as string) || i}
                      className="border border-gray-100 rounded-lg p-3 bg-gray-50/80"
                    >
                      <div className="font-medium text-gray-900">
                        {recentLabel(t)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(t.location as string) ||
                          (t.zone as string) ||
                          "—"}{" "}
                        ·{" "}
                        {new Date(
                          (t.incidentDateTime as string) ||
                            (t.accessDateTime as string) ||
                            (t.detectedAt as string) ||
                            (t.alertDateTime as string) ||
                            Date.now()
                        ).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Physical–cyber correlation
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Matches security incidents with denied access in the same zone and
              with cyber alerts (time-aligned; zone/location when present) within
              the window.{" "}
              {correlation?.cyberAlertCount != null && (
                <span className="text-gray-600">
                  {correlation.cyberAlertCount} cyber alert(s) in the last{" "}
                  {corrWindow} minutes.
                </span>
              )}
            </p>
            {!correlation?.correlations?.length ? (
              <p className="text-gray-500 text-sm">
                No correlations in the last {corrWindow} minutes. Add incidents,
                denied access, and/or cyber alerts that share time and area.
              </p>
            ) : (
              <div className="space-y-4">
                {correlation.correlations.map((row: CyberPhysicalCorrelationRow, idx) => {
                  const pi = row.physicalIncident as Record<string, unknown>;
                  return (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50/50"
                    >
                      <div className="flex flex-wrap justify-between gap-2 mb-2">
                        <span className="font-semibold text-gray-900">
                          {String(pi.incidentType || "Incident")}
                        </span>
                        <span className="text-xs bg-[#EAFBFA] text-[#0fa9a6] px-2 py-0.5 rounded-full">
                          Score {row.correlationScore}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">
                        {String(pi.location || pi.zone || "")} ·{" "}
                        {new Date(
                          String(pi.incidentDateTime || pi.createdAt || "")
                        ).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-700">
                        Related denied access: {row.relatedAccessViolations.length}{" "}
                        · IT / NIDS / SIEM: {row.relatedITAlerts.length}
                      </p>
                      {row.relatedITAlerts.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                          {row.relatedITAlerts.slice(0, 5).map((a, j) => (
                            <li key={j}>
                              {(a.title as string) || "Alert"}{" "}
                              <span className="text-gray-400">
                                (
                                {categoryBadge(
                                  a.alertCategory as string | undefined
                                )}
                                )
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Server className="w-5 h-5 text-gray-500" />
                Access system logs (denied)
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Latest denied access events from the access log service.
              </p>
              <div className="overflow-x-auto max-h-64 overflow-y-auto text-xs">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="p-2">Time</th>
                      <th className="p-2">Zone</th>
                      <th className="p-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deniedLogs.map((log, i) => (
                      <tr key={(log.id as string) || i} className="border-t">
                        <td className="p-2 whitespace-nowrap">
                          {log.accessDateTime
                            ? new Date(String(log.accessDateTime)).toLocaleString()
                            : "—"}
                        </td>
                        <td className="p-2">
                          {String(log.zone || log.location || "—")}
                        </td>
                        <td className="p-2">{String(log.accessType || "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {deniedLogs.length === 0 && (
                  <p className="text-gray-500 p-4">No denied access rows.</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Radio className="w-5 h-5 text-gray-500" />
                IT security {"&"} network intrusion alerts
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                Alerts are stored per tenant and feed the unified dashboard and
                correlation engine. SIEM or NIDS connectors should POST into the
                cyber-alert API (authenticated). Use{" "}
                <button
                  type="button"
                  className="text-[#0fa9a6] underline"
                  onClick={() => navigate("/security/integrations")}
                >
                  Security Integration &amp; Interoperability
                </button>{" "}
                for channel overview.
              </p>
              <div className="overflow-x-auto max-h-52 overflow-y-auto text-xs mb-4">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="p-2">Time</th>
                      <th className="p-2">Type</th>
                      <th className="p-2">Title</th>
                      <th className="p-2">Sev</th>
                      <th className="p-2">Zone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cyberAlerts.map((a, i) => (
                      <tr key={a.id || a._id || i} className="border-t">
                        <td className="p-2 whitespace-nowrap">
                          {a.alertDateTime
                            ? new Date(a.alertDateTime).toLocaleString()
                            : "—"}
                        </td>
                        <td className="p-2">
                          {categoryBadge(a.alertCategory)}
                        </td>
                        <td className="p-2 max-w-[140px] truncate">
                          {a.title || "—"}
                        </td>
                        <td className="p-2">{a.severity || "—"}</td>
                        <td className="p-2">
                          {a.zone || a.location || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {cyberAlerts.length === 0 && (
                  <p className="text-gray-500 p-4 text-sm">
                    No cyber alerts in store yet. Log one below or ingest from a
                    connector.
                  </p>
                )}
              </div>
              <form
                onSubmit={submitInternalAlert}
                className="rounded-lg border border-gray-200 p-3 space-y-2 text-xs"
              >
                <p className="font-medium text-gray-800 text-sm">
                  Log internal / test alert
                </p>
                <input
                  className="w-full border rounded px-2 py-1"
                  placeholder="Title"
                  value={logForm.title}
                  onChange={(e) =>
                    setLogForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
                <div className="flex flex-wrap gap-2">
                  <select
                    className="border rounded px-2 py-1"
                    value={logForm.alertCategory}
                    onChange={(e) =>
                      setLogForm((f) => ({
                        ...f,
                        alertCategory: e.target.value as CyberAlertCategory,
                      }))
                    }
                  >
                    <option value="it_security">IT security</option>
                    <option value="network_intrusion">Network intrusion</option>
                    <option value="siem_other">SIEM / other</option>
                  </select>
                  <select
                    className="border rounded px-2 py-1"
                    value={logForm.severity}
                    onChange={(e) =>
                      setLogForm((f) => ({ ...f, severity: e.target.value }))
                    }
                  >
                    {["Low", "Medium", "High", "Critical"].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    className="flex-1 min-w-[120px] border rounded px-2 py-1"
                    placeholder="Zone (optional, for correlation)"
                    value={logForm.zone}
                    onChange={(e) =>
                      setLogForm((f) => ({ ...f, zone: e.target.value }))
                    }
                  />
                  <input
                    className="flex-1 min-w-[100px] border rounded px-2 py-1"
                    placeholder="Source"
                    value={logForm.source}
                    onChange={(e) =>
                      setLogForm((f) => ({ ...f, source: e.target.value }))
                    }
                  />
                </div>
                <button
                  type="submit"
                  disabled={logging}
                  className="px-3 py-1.5 bg-[#0fa9a6] text-white rounded text-xs hover:bg-teal-600 disabled:opacity-50"
                >
                  {logging ? "Saving…" : "Record alert"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CyberPhysicalHub;
